export interface AssetLiveDataUpdate {
  id: number;
  n: string | null;
  hw: number;
  ts: string;
  lat: number | null;
  lon: number | null;
  hd: number | null;
  sp: number | null;
  flt1: number;
  flt2: number;
}

export interface SiteLiveDataUpdate {
  r: boolean; // replace
  a: AssetLiveDataUpdate[];
}

interface StreamingInterest {
  assets: boolean;
}

interface StreamingRequest {
  interest?: StreamingInterest;
}

export class CasStreamingSocketClient {
  private readonly _connectionTimeoutMillis: number = 10000;
  private readonly _pingIntervalMillis: number = 30000;
  private readonly _reconnectIntervalMillis: number = 2000;

  private readonly _url: string = '';

  private _client?: WebSocket;
  private _lockConnect = false;
  private _lockReConnect = false;
  private _totalRetry = 0;
  private _reconnectInterval: NodeJS.Timeout | undefined = undefined;
  private _pingInterval: NodeJS.Timeout | undefined = undefined;

  private _interest: StreamingInterest = {
    assets: false,
  };

  constructor(url: string) {
    this._url = url;
  }

  public onConnState: ((event: boolean) => void) | null = null;
  public onAssetUpdates: ((event: SiteLiveDataUpdate) => void) | null = null;

  public getConnState() {
    return this._client?.readyState === 1;
  }

  public setAssetInterest(state: boolean) {
    this._interest.assets = state;
    this._sendInterests();
  }

  public connect(): void {
    return this._connect();
  }

  public disconnect(): void {
    return this._disconnect();
  }

  private _connect = () => {
    console.debug(`connecting to ${this._url}..`);
    if (!this._lockConnect) {
      this._totalRetry++;
      this._lockConnect = true;

      this._client = new WebSocket(this._url);

      console.debug(`websocket created with status ${this._client?.readyState}`);
      this._addEventListeners();
      console.debug('added event listeners');

      setTimeout(() => {
        if (this._client?.readyState === 0 && !this._lockReConnect) {
          console.debug(`timeout reached (${this._connectionTimeoutMillis})`);
          clearInterval(this._reconnectInterval);
          const error = new ErrorEvent('timeout', undefined);
          this._onerror(error);
        }
      }, this._connectionTimeoutMillis);

      clearInterval(this._pingInterval);

      this._pingInterval = setInterval(() => {
        if (this._client?.readyState === 1) {
          console.debug('sending ping to server to keep alive');
          const request: StreamingRequest = {};
          this._client?.send(JSON.stringify(request));
        }
      }, this._pingIntervalMillis);
    }
  };

  private _reconnect = () => {
    this._lockConnect = false;
    console.debug('setting reconnect interval');
    this._reconnectInterval = setInterval(() => {
      this._connect();
    }, this._reconnectIntervalMillis);
  };

  private _onopen = (event: Event) => {
    this._lockConnect = false;
    this._lockReConnect = false;
    this._totalRetry = 0;

    clearInterval(this._reconnectInterval);

    // Always send on connect to ensure new server threads are consistent
    this._sendInterests();

    if (this.onConnState) {
      this.onConnState(this._client?.readyState === 1);
    }
  };

  private _onclose = (event: CloseEvent) => {
    this._lockConnect = false;

    if (this.onConnState) {
      this.onConnState(this._client?.readyState === 1);
    }

    if (!this._lockReConnect && this._client?.readyState === 3) {
      this._lockReConnect = true;
      console.debug('starting reconnect after closing');
      this._reconnect();
    }
  };

  private _onmessage = (event: MessageEvent) => {
    console.debug(`message received`, event.data, this.onAssetUpdates);

    const data: SiteLiveDataUpdate = JSON.parse(event.data as string);
    if (data === undefined) {
      console.debug('Bad data received');
      return;
    }

    // Raise the appropriate events
    if (this.onAssetUpdates && data) {
      this.onAssetUpdates(data);
    }
  };

  private _onerror = (event: Event) => {
    console.debug('error state in connection, will disconnect', event.type);
    this._lockConnect = false;
    this._disconnect(undefined, event.type);

    if (!this._lockReConnect && this._client?.readyState === 3) {
      this._lockReConnect = true;
      console.debug('starting reconnect after error');
      this._reconnect();
    }
  };

  private _disconnect(code = 1000, reason?: string) {
    console.debug('disconnecting client');

    if (!this._client) {
      return;
    }

    this._client.close(code, reason);
    this._onclose(new CloseEvent(reason ?? 'disconnect', undefined));
  }

  private _sendInterests() {
    if (this._client?.readyState === 1) {
      console.debug('sending interests to server');
      const request: StreamingRequest = {
        interest: this._interest,
      };
      this._client?.send(JSON.stringify(request));
    }
  }

  private _addEventListeners() {
    if (!this._client) {
      return;
    }

    this._client.addEventListener('open', this._onopen);
    this._client.addEventListener('close', this._onclose);
    this._client.addEventListener('message', this._onmessage);
    this._client.addEventListener('error', this._onerror);
  }
}
